import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { 
  Wallet, ArrowUpRight, ArrowDownLeft, TrendingUp, 
  Clock, ChevronRight, Plus, RefreshCw, PieChart, DollarSign, Shield, Activity, Zap
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CryptoTicker from '../components/CryptoTicker';
import TrustBadges from '../components/TrustBadges';
import LiveTradeActivity from '../components/LiveTradeActivity';
import { useAuth } from '../context/AuthContext';
import { getUserDashboard, getPlatformSettings } from '../services/api';
import { toast } from 'sonner';

const useAnimatedCounter = (end, duration = 1.5, decimals = 2) => {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (isInView && !hasAnimated.current && end > 0) {
      hasAnimated.current = true;
      const startTime = performance.now();
      const animate = (now) => {
        const progress = Math.min((now - startTime) / (duration * 1000), 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setValue(end * eased);
        if (progress < 1) requestAnimationFrame(animate);
        else setValue(end);
      };
      requestAnimationFrame(animate);
    }
  }, [isInView, end, duration]);

  return { ref, value: value.toFixed(decimals) };
};

// =================== LIVE PROFIT TRACKER ===================
const LiveProfitTracker = ({ activeTradingBalance, dailyProfitRate, todayConfirmedProfit = 0 }) => {
  const [earnings, setEarnings] = useState(0);
  const animRef = useRef(null);
  const earningsRef = useRef(0);

  const dailyProfit = activeTradingBalance * (dailyProfitRate / 100);
  const perSecond = dailyProfit / 86400;

  const getSecsSinceMidnightUTC = useCallback(() => {
    const now = new Date();
    const midnight = new Date(now);
    midnight.setUTCHours(0, 0, 0, 0);
    return (now - midnight) / 1000;
  }, []);

  const getDayProgressPercent = useCallback(() => {
    return (getSecsSinceMidnightUTC() / 86400) * 100;
  }, [getSecsSinceMidnightUTC]);

  useEffect(() => {
    if (activeTradingBalance <= 0 || dailyProfitRate <= 0) {
      setEarnings(0);
      return;
    }

    const startValue = perSecond * getSecsSinceMidnightUTC();
    earningsRef.current = startValue;
    setEarnings(startValue);

    let lastTs = performance.now();
    const tick = (now) => {
      const dt = (now - lastTs) / 1000;
      lastTs = now;
      earningsRef.current += perSecond * dt;

      // Reset at midnight
      if (getSecsSinceMidnightUTC() < 1) earningsRef.current = 0;

      setEarnings(earningsRef.current);
      animRef.current = requestAnimationFrame(tick);
    };
    animRef.current = requestAnimationFrame(tick);

    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [activeTradingBalance, dailyProfitRate, perSecond, getSecsSinceMidnightUTC]);

  if (activeTradingBalance <= 0) return null;

  const fmt = (v) => {
    const abs = Math.abs(v);
    if (abs < 0.01) return v.toFixed(6);
    if (abs < 1) return v.toFixed(4);
    return v.toFixed(2);
  };

  const dayProgress = getDayProgressPercent();
  const perMinute = perSecond * 60;
  const perHour = perSecond * 3600;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.09 }}
      className="relative rounded-2xl overflow-hidden"
      data-testid="live-profit-tracker"
    >
      {/* Green glow border */}
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-emerald-500/20 via-green-500/10 to-emerald-500/20 p-[1px]">
        <div className="w-full h-full rounded-2xl bg-[#0a0f1a]" />
      </div>

      <div className="relative p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          {/* Left: Main counter */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs font-semibold uppercase tracking-widest text-emerald-400">
                Live Earnings Today
              </span>
              <Zap className="w-3.5 h-3.5 text-emerald-400" />
            </div>
            <div className="flex items-baseline gap-1" data-testid="live-earnings-value">
              <span className="text-sm text-emerald-400/70 font-medium">+$</span>
              <span
                className="text-4xl sm:text-5xl font-bold font-['Outfit'] text-emerald-400 tabular-nums"
                style={{
                  textShadow: '0 0 30px rgba(16,185,129,0.3), 0 0 60px rgba(16,185,129,0.15)',
                }}
              >
                {fmt(earnings)}
              </span>
            </div>
            {todayConfirmedProfit > 0 && (
              <p className="text-xs text-emerald-400/50 mt-1.5">
                Includes ${todayConfirmedProfit.toFixed(2)} confirmed profit distributed today
              </p>
            )}
          </div>

          {/* Right: Rate breakdown */}
          <div className="flex flex-row sm:flex-col gap-4 sm:gap-3 sm:items-end sm:text-right">
            <div>
              <p className="text-[10px] text-slate-600 uppercase tracking-wider font-medium">Per Minute</p>
              <p className="text-sm font-semibold text-white tabular-nums">+${perMinute.toFixed(4)}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-600 uppercase tracking-wider font-medium">Per Hour</p>
              <p className="text-sm font-semibold text-white tabular-nums">+${perHour.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-600 uppercase tracking-wider font-medium">Est. Today</p>
              <p className="text-sm font-bold text-emerald-400 tabular-nums">+${dailyProfit.toFixed(2)}</p>
            </div>
          </div>
        </div>

        {/* Day progress bar */}
        <div className="mt-5">
          <div className="flex items-center justify-between text-[10px] text-slate-600 mb-1.5">
            <span>00:00 UTC</span>
            <span>{dayProgress.toFixed(1)}% of trading day</span>
            <span>23:59 UTC</span>
          </div>
          <div className="h-1 rounded-full bg-white/[0.04] overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-green-400"
              initial={{ width: 0 }}
              animate={{ width: `${dayProgress}%` }}
              transition={{ duration: 1, ease: 'easeOut' }}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const DashboardPage = () => {
  const { user, refreshUser } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tickerKey, setTickerKey] = useState(0);

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const [dashRes, settingsRes] = await Promise.all([
        getUserDashboard(), getPlatformSettings()
      ]);
      setDashboardData(dashRes.data);
      setSettings(settingsRes.data);
      await refreshUser();
      if (isRefresh) {
        setTickerKey(k => k + 1);
        toast.success('Dashboard refreshed');
      }
    } catch { toast.error('Failed to load dashboard'); }
    finally { setLoading(false); setRefreshing(false); }
  };

  const buildChartData = () => {
    if (!dashboardData) return [];
    const base = dashboardData?.balance || 0;
    const rate = parseFloat(settings.daily_profit_percentage || 3) / 100;
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (6 - i));
      return { name: d.toLocaleDateString('en-US', { weekday: 'short' }), value: Math.max(0, base - base * rate * (6 - i)) };
    });
  };

  const chartData = buildChartData();
  const dailyProfitRate = parseFloat(settings.daily_profit_percentage || 3);
  const formatCurrency = (a) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(a || 0);
  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'deposit': return <ArrowDownLeft className="w-4 h-4 text-emerald-400" />;
      case 'withdrawal': return <ArrowUpRight className="w-4 h-4 text-orange-400" />;
      case 'investment': return <TrendingUp className="w-4 h-4 text-cyan-400" />;
      case 'profit': case 'daily_profit': return <Plus className="w-4 h-4 text-emerald-400" />;
      default: return <Clock className="w-4 h-4 text-slate-500" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen"><div className="animated-bg" /><Navbar />
        <div className="pt-32 px-6 flex items-center justify-center">
          <div className="w-10 h-10 border-3 border-cyan-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  // Active trading balance = invested_amount (deposits confirmed and trading)
  const activeTradingBalance = dashboardData?.invested_amount || 0;

  return (
    <div className="min-h-screen"><div className="animated-bg" /><div className="noise-overlay" /><Navbar />
      <main className="pt-28 sm:pt-32 pb-20 px-4 sm:px-6 overflow-x-hidden" data-testid="user-dashboard">
        <div className="max-w-7xl mx-auto space-y-6 sm:space-y-8">

          {/* Header + Trust */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] text-white">
                Welcome back, {user?.name?.split(' ')[0]}
              </h1>
              <div className="flex items-center gap-3 mt-2">
                <span className="flex items-center gap-1.5 text-xs text-emerald-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Automated Trading Active
                </span>
                <span className="text-slate-600 text-xs">|</span>
                <span className="text-xs text-slate-500">Last updated: just now</span>
              </div>
            </div>
            <button
              onClick={() => fetchDashboard(true)}
              disabled={refreshing}
              className="btn-secondary flex items-center gap-2 !py-2.5 !px-5 !text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="refresh-dashboard"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </motion.div>

          {/* Crypto Ticker */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <CryptoTicker refreshKey={tickerKey} />
          </motion.div>

          {/* Automated Trading Info Banner */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
            className="flex items-start gap-3 px-5 py-4 rounded-xl bg-cyan-500/5 border border-cyan-500/10" data-testid="auto-trading-banner"
          >
            <Activity className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-white font-medium">Fully Automated Trading</p>
              <p className="text-xs text-slate-400 mt-1">All confirmed deposits are automatically activated for trading and begin generating profits immediately. No manual action required.</p>
            </div>
          </motion.div>

          {/* Live Profit Tracker */}
          <LiveProfitTracker
            activeTradingBalance={activeTradingBalance}
            dailyProfitRate={dailyProfitRate}
            todayConfirmedProfit={dashboardData?.today_confirmed_profit || 0}
          />

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            <StatCard label="Available Balance" amount={dashboardData?.balance || 0} icon={Wallet} gradient="from-cyan-500/10 to-blue-500/10" iconColor="text-cyan-400" glow="text-white" sub="Available for withdrawal" delay={0.1} testId="total-balance" />
            <StatCard label="Active Trading Balance" amount={activeTradingBalance} icon={Activity} gradient="from-purple-500/10 to-cyan-500/10" iconColor="text-purple-400" sub={`Earning ${dailyProfitRate}% daily`} subColor="text-emerald-400" delay={0.15} testId="active-trading-balance" />
            <StatCard label="Total Profit" amount={dashboardData?.total_profit || 0} icon={Plus} gradient="from-emerald-500/10 to-green-500/10" iconColor="text-emerald-400" glow="text-emerald-400" sub="Lifetime earnings" delay={0.2} testId="total-profit" />
            <StatCard label="Total Withdrawn" amount={dashboardData?.total_withdrawn || 0} icon={ArrowUpRight} gradient="from-orange-500/10 to-red-500/10" iconColor="text-orange-400" sub="Successfully paid out" delay={0.25} testId="total-withdrawn" />
          </div>

          {/* Quick Actions */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {[
              { name: 'Deposit', path: '/deposit', icon: ArrowDownLeft, c: 'from-emerald-500 to-green-600' },
              { name: 'Withdraw', path: '/withdraw', icon: ArrowUpRight, c: 'from-orange-500 to-red-500' },
              { name: 'Portfolio', path: '/portfolio', icon: PieChart, c: 'from-purple-500 to-pink-500' },
              { name: 'Profile', path: '/profile', icon: DollarSign, c: 'from-cyan-500 to-blue-500' },
            ].map((a) => (
              <Link key={a.name} to={a.path} className="flex items-center gap-3 px-4 py-4 rounded-xl border border-white/[0.04] bg-white/[0.015] hover:bg-white/[0.04] hover:border-white/[0.08] transition-all group" data-testid={`quick-action-${a.name.toLowerCase()}`}>
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${a.c} flex items-center justify-center group-hover:scale-105 transition-transform`}>
                  <a.icon className="w-5 h-5 text-white" />
                </div>
                <span className="text-white font-medium text-sm">{a.name}</span>
                <ChevronRight className="w-4 h-4 text-slate-600 ml-auto group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ))}
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-5 sm:gap-6">
            {/* Chart */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="lg:col-span-2 rounded-2xl border border-white/[0.06] bg-[#0a0f1a]/80 p-5 sm:p-7">
              <h2 className="text-lg font-bold text-white mb-5">Trading Performance</h2>
              <div className="h-56 sm:h-64" data-testid="portfolio-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#00F0FF" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#00F0FF" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#475569', fontSize: 11 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#475569', fontSize: 11 }} tickFormatter={(v) => `$${v.toLocaleString()}`} width={60} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#0a0f1a', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', color: '#fff', fontSize: '13px', padding: '8px 12px' }}
                      formatter={(v) => [`$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}`, 'Value']}
                    />
                    <Area type="monotone" dataKey="value" stroke="#00F0FF" strokeWidth={2.5} fillOpacity={1} fill="url(#chartGrad)" animationDuration={1500} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Trading Status Summary */}
              {activeTradingBalance > 0 && (
                <div className="mt-5 p-4 sm:p-5 rounded-xl bg-white/[0.03] border border-white/[0.05]">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    <h3 className="text-white font-semibold text-sm">Trading Active</h3>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Trading Balance</p>
                      <p className="text-lg font-bold text-white">{formatCurrency(activeTradingBalance)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Daily Return Rate</p>
                      <p className="text-lg font-bold text-emerald-400">{dailyProfitRate}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Est. Daily Profit</p>
                      <p className="text-lg font-bold text-emerald-400">+{formatCurrency(activeTradingBalance * dailyProfitRate / 100)}</p>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>

            {/* Recent Activity */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="rounded-2xl border border-white/[0.06] bg-[#0a0f1a]/80 p-5 sm:p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold text-white">Recent Activity</h2>
                <Link to="/portfolio" className="text-cyan-400 text-xs font-medium hover:text-cyan-300 transition-colors">View all</Link>
              </div>
              <div className="space-y-2.5" data-testid="activity-feed">
                {dashboardData?.recent_transactions?.length > 0 ? (
                  dashboardData.recent_transactions.slice(0, 8).map((tx, i) => (
                    <motion.div key={tx.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.45 + i * 0.04 }}
                      className="flex items-center gap-3 py-3 px-3 rounded-xl border border-white/[0.03] bg-white/[0.01] hover:bg-white/[0.03] transition-colors"
                    >
                      <div className="w-8 h-8 rounded-lg bg-white/[0.04] flex items-center justify-center flex-shrink-0">
                        {getTransactionIcon(tx.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{tx.description}</p>
                        <p className="text-slate-600 text-xs">{formatDate(tx.created_at)}</p>
                      </div>
                      <p className={`text-sm font-semibold whitespace-nowrap ${tx.amount >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {tx.amount >= 0 ? '+' : ''}{formatCurrency(tx.amount)}
                      </p>
                    </motion.div>
                  ))
                ) : (
                  <div className="text-center py-10">
                    <Clock className="w-10 h-10 mx-auto mb-3 text-slate-700" />
                    <p className="text-sm text-slate-500">No recent transactions</p>
                    <Link to="/deposit" className="text-cyan-400 text-xs mt-2 inline-block hover:text-cyan-300">Make your first deposit</Link>
                  </div>
                )}
              </div>
            </motion.div>
          </div>

          {/* Live Trade Activity + Trust */}
          <div className="grid lg:grid-cols-3 gap-5 sm:gap-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
              className="lg:col-span-2 rounded-2xl border border-white/[0.06] bg-[#0a0f1a]/80 p-5 sm:p-6"
            >
              <LiveTradeActivity />
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
              className="rounded-2xl border border-white/[0.06] bg-[#0a0f1a]/80 p-5 sm:p-6"
            >
              <h2 className="text-lg font-bold text-white mb-4">Security & Trust</h2>
              <TrustBadges />
              <div className="mt-4 p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/10">
                <p className="text-xs text-emerald-400 font-medium">Transactions verified securely</p>
                <p className="text-xs text-slate-500 mt-1">All platform operations are encrypted and monitored 24/7</p>
              </div>
            </motion.div>
          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
};

const StatCard = ({ label, amount, icon: Icon, gradient, iconColor, glow = 'text-white', sub, subColor = 'text-slate-500', delay, testId }) => {
  const counter = useAnimatedCounter(amount, 1.5, 2);
  return (
    <motion.div ref={counter.ref} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }} className="stat-card">
      <div className="flex items-center justify-between mb-3">
        <span className="text-slate-500 text-xs font-medium uppercase tracking-wider">{label}</span>
        <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center`}>
          <Icon className={`w-4.5 h-4.5 ${iconColor}`} />
        </div>
      </div>
      <p className={`text-2xl sm:text-3xl font-bold ${glow}`} data-testid={testId}>
        ${Number(counter.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </p>
      <p className={`text-xs mt-2 ${subColor}`}>{sub}</p>
    </motion.div>
  );
};

export default DashboardPage;
